"use client";

import { useEffect } from "react";

declare global {
  interface Window {
    __translationDomGuardApplied?: boolean;
  }
}

/**
 * Google Translate (Chrome) envolve text nodes em <font>, mutando o DOM por
 * fora do React. Em re-renders (carrossel da hero, toggles), o React tenta
 * remover/inserir nós que não são mais filhos de quem ele acha que são e
 * lança NotFoundError em removeChild/insertBefore — derrubando a árvore no
 * error boundary (violação 2.1.1 da review da Shopify App Store).
 *
 * Patch canônico: torna as duas operações tolerantes ao nó órfão em vez de
 * lançar. Não interfere em nenhum caminho válido (parent correto → nativo).
 */
export function TranslationDomGuard() {
  useEffect(() => {
    if (window.__translationDomGuardApplied) return;
    window.__translationDomGuardApplied = true;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
      if (child.parentNode !== this) {
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
      this: Node,
      newNode: T,
      referenceNode: Node | null
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        return this.appendChild(newNode) as T;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
  }, []);

  return null;
}
